import type { Choice } from "@fobu/schema";

/** Plan für „Zeitfenster buchen“: von–bis, Wochentage, Uhrzeiten, Dauer. Alles lokale Zeit (Europe/Berlin). */
export interface SlotPlan {
  /** Erster und letzter Tag, YYYY-MM-DD. */
  from: string;
  to: string;
  /** 1 = Montag … 7 = Sonntag. */
  weekdays: number[];
  /** Tagesbeginn und -ende, HH:MM. */
  start: string;
  end: string;
  /** Dauer eines Zeitfensters in Minuten. */
  duration: number;
  /** Pause zwischen zwei Zeitfenstern in Minuten. */
  gap?: number;
  /** Plätze je Zeitfenster (leer = unbegrenzt). */
  capacity?: number;
  location?: string;
}

export const MAX_SLOTS = 400;
const pad = (n: number) => String(n).padStart(2, "0");
const minutes = (hhmm: string) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  return m ? Number(m[1]) * 60 + Number(m[2]) : NaN;
};
const hhmm = (min: number) => `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;

/** „Mo., 12.10.2026, 09:00–09:30“ – lesbar in Exporten, Mails und Auswertungen. */
export function slotLabel(startsAt: string, endsAt?: string, lang = "de"): string {
  const d = new Date(`${startsAt.slice(0, 10)}T12:00:00Z`);
  const day = new Intl.DateTimeFormat(lang.startsWith("de") ? "de-DE" : lang, { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" }).format(d);
  return `${day}, ${startsAt.slice(11, 16)}${endsAt ? `–${endsAt.slice(11, 16)}` : ""}`;
}

/** Zeitfenster aus einem Plan erzeugen (höchstens MAX_SLOTS). */
export function generateSlots(plan: SlotPlan, makeId: () => string, lang = "de"): Choice[] {
  const out: Choice[] = [];
  const startMin = minutes(plan.start);
  const endMin = minutes(plan.end);
  const step = Math.max(5, Math.round(plan.duration)) + Math.max(0, Math.round(plan.gap ?? 0));
  const dur = Math.max(5, Math.round(plan.duration));
  if (!Number.isFinite(startMin) || !Number.isFinite(endMin) || endMin <= startMin) return out;
  const first = new Date(`${plan.from}T00:00:00Z`);
  const last = new Date(`${plan.to}T00:00:00Z`);
  if (Number.isNaN(first.getTime()) || Number.isNaN(last.getTime())) return out;
  for (let d = first; d <= last && out.length < MAX_SLOTS; d = new Date(d.getTime() + 86_400_000)) {
    const wd = d.getUTCDay() || 7;
    if (!plan.weekdays.includes(wd)) continue;
    const date = d.toISOString().slice(0, 10);
    for (let m = startMin; m + dur <= endMin && out.length < MAX_SLOTS; m += step) {
      const startsAt = `${date}T${hhmm(m)}`;
      const endsAt = `${date}T${hhmm(m + dur)}`;
      out.push({
        id: makeId(),
        label: slotLabel(startsAt, endsAt, lang),
        startsAt,
        endsAt,
        ...(plan.capacity ? { capacity: plan.capacity } : {}),
        ...(plan.location ? { location: plan.location } : {}),
      });
    }
  }
  return out;
}
